<template>
    <client-only>
        <sup
            ref="target"
            @mouseover="op?.show"
            @mouseout="op?.hide"
        >
            <NuxtLink
                v-if="outsideLink"
                :to="outsideLink"
                external
                target="_blank"
                class="!border-b-0 !no-underline"
            >
                *
            </NuxtLink>
            <span v-else>*</span>
        </sup>
        <OverlayPanel
            ref="op"
            :pt
        >
            <div class="flex flex-col gap-2">
                <span
                    v-html="
                        footnoteContent[0]?.FOOTNOTE.replaceAll(
                            '🡨.',
                            ''
                        ).replaceAll('🡨', '')
                    "
                />
            </div>
        </OverlayPanel>
    </client-only>
</template>

<script lang="ts" setup>
import OverlayPanel from "primevue/overlaypanel"
import { useElementVisibility, watchOnce } from "@vueuse/core"

const props = defineProps<{
    text: string
    tema: TemaItem
}>()

const op = ref<OverlayPanel | null>(null)

const { locale } = useI18n()
const store = useMainStore()

const target = ref<HTMLElement | null>(null)
const targetIsVisible = useElementVisibility(target)

watchOnce(targetIsVisible, () => {
    if (targetIsVisible) {
        getFootnoteContent()
    }
})

// const footnoteId = props.text.match(/truefootnote-ref-(\d+)/)?.[1]
const footnoteId = props.text.match(/truefootnote-(\d+)/)?.[1]

const FOOTNOTE_ENDPOINT = "footnotes"

const footnoteContent = ref<FootnoteItem[]>([])

async function getFootnoteContent() {
    if (footnoteContent.value.length > 0) return
    const data = await $fetch<FootnoteItem[]>(
        `${FOOTNOTE_ENDPOINT}/${props.tema.KNIHA_ID}/${locale.value === "cs" ? "cz" : locale.value}/${footnoteId}`,
        {
            baseURL: import.meta.client
                ? store.apiBaseUrl
                : store.apiBaseUrlInt,
        }
    )
    footnoteContent.value = data
    return data
}

const outsideLink = computed(() => {
    const link = footnoteContent.value[0]?.FOOTNOTE.replaceAll("🡨.", "")
        .replaceAll("🡨", "")
        .match(/((https:\/\/|www)[^\s]+)/)?.[1]
    if (!link) return null
    if (!link.includes("https://")) {
        return "https://" + link
    }
    return link
})

const pt = {
    root: {
        class: [
            // Shape
            "rounded-md shadow-lg max-w-[50vw] md:max-w-[25rem]",
            "border-0",

            // Position
            "absolute -translate-x-5 top-0 mt-2",
            "z-40 transform origin-center",

            // Color
            "bg-white",
            "text-black-text",

            // Before: Triangle
            "before:absolute before:-top-[9px] before:-ml-[9px] before:left-[calc(var(--overlayArrowLeft,0)+0.75rem)] z-0",
            "before:w-0 before:h-0",
            "before:border-transparent before:border-solid",
            "before:border-x-[4px] before:border-[4px]",
            "before:border-t-0 before:border-b-gray-300/10 ",

            "after:absolute after:-top-1 after:-ml-[12px] after:left-[calc(var(--overlayArrowLeft,0)+0.75rem)]",
            "after:w-0 after:h-0",
            "after:border-transparent after:border-solid",
            "after:border-x-[0.25rem] after:border-[0.25rem]",
            "after:border-t-0 after:border-b-black-text ",
        ],
    },
    content: {
        class: "p-2 border text-sm items-center flex bg-white border border-black-text",
    },
    transition: {
        enterFromClass: "opacity-0 scale-y-[0.8]",
        enterActiveClass:
            "transition-[transform,opacity] duration-[120ms] ease-[cubic-bezier(0,0,0.2,1)]",
        leaveActiveClass: "transition-opacity duration-100 ease-linear",
        leaveToClass: "opacity-0",
    },
}
</script>

<style scoped></style>
